import { Editor, JSONContent } from '@tiptap/core';
import { StreamAPI } from 'api/stream';
import { streamTipTapNodes } from 'helpers/stream';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { LoaderStreamingComponent } from 'components/common/loader-streaming';
import { DocumentEditor } from 'components/organisms/document-editor/document-editor';
import { fragmentJSONToEditorContent } from 'components/organisms/document-editor/utils/node-utils';
import { removeAllAskAiSelectionHighlightsCommand } from '../../../../aski-ai-selection-highlight/utils';
import { AskAiCommandName } from '../../../commands/command';
import { AskAiCommand, AskAiRequestPayload } from '../../../commands/types';
import { InProgressStorageManager } from '../../../commands/utils/inprogress-storage-manager';
import {
  addAskAiSelectionHighlightToReplacedContentCommand,
  replaceSelectionWithContentCommand,
} from '../../../commands/utils/replace-content';
import { AskAiToolbar } from '../ask-ai-toolbar';
import { OutputSectionFooter } from '../footer';
import { Section } from '../section-ui';
import { SectionTitle } from '../section-ui/title';
import { getSectionTitle, getSectionTitleIcon } from '../title-and-icon';

const OutputSection = styled(Section)`
  scroll-margin-top: 50px;
  scroll-margin-bottom: 50px;
`;

interface StreamingInfo {
  promptIdList: string[];
  streamingMeta: {
    token: string;
    channelName: string;
  };
  response: {
    content: JSONContent[];
  };
}
interface Props {
  loading: boolean;
  parentEditor: Editor;
  request: AskAiRequestPayload;
  processedContent: JSONContent[];
  streamingInfo: StreamingInfo;
  onNewRequest: (request: AskAiRequestPayload, editor: Editor) => void;
  onAction: (content: JSONContent[]) => void;
  renderOriginalContentWithDifference?: boolean;
}
export const OutputContentSection: FC<Props> = ({
  loading,
  streamingInfo,
  parentEditor,
  request,
  processedContent,
  onNewRequest,
  onAction,
  renderOriginalContentWithDifference = false,
}) => {
  const [isInitializingStreaming, setIsInitializingStreaming] = useState(false);
  // Always start with streaming so the channel is established regardless of
  // whether this is the first or a subsequent (renderOriginalContentWithDifference)
  // request.  Previously this was initialised from renderOriginalContentWithDifference
  // which caused the streaming editor to be skipped entirely for follow-up runs.
  const [streamEnded, setStreamEnded] = useState(false);
  const [streamContent, setStreamContent] = useState<JSONContent[]>([]);
  const [resultEditor, setResultEditor] = useState<Editor | null>(null);
  const parentEditorAskAiCommandExtension = useMemo(
    () =>
      parentEditor.extensionManager.extensions.find(
        (extension) => extension.name === AskAiCommandName,
      ),
    [parentEditor],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const focusOnContainer = useCallback(() => {
    if (containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        window.scrollBy(0, -50);
      }, 100);
    }
  }, [containerRef]);

  const onResultEditorInitialized = useCallback(
    async (editor: Editor) => {
      setResultEditor(editor);

      const parentEditorContent = parentEditor.state.doc;
      const sizeBetweenSelection =
        request.selection.to - request.selection.from;
      const isEntireDocumentSelected =
        (request.selection.from === 0 &&
          request.selection.to === parentEditorContent.content.size - 1) ||
        sizeBetweenSelection === parentEditorContent.textContent.length;

      if (renderOriginalContentWithDifference && !isEntireDocumentSelected) {
        // Prefer the post-stream content (set by onEditorInitializedForStream after
        // the streaming channel completes); fall back to processedContent for cases
        // where streaming didn't produce a final payload.
        const diffContent =
          streamContent.length > 0 ? streamContent : processedContent;

        editor.commands.setContent(parentEditor.getJSON());
        editor
          .chainWithAskAiActionMeta()
          .command(removeAllAskAiSelectionHighlightsCommand())
          .command(
            replaceSelectionWithContentCommand(
              structuredClone(diffContent),
              request.selection.from,
              request.selection.to,
              editor.extensionManager.extensions.find(
                (extension) => extension.name === AskAiCommandName,
              )?.options.contentReplacementOptions,
            ),
          )
          .command(
            addAskAiSelectionHighlightToReplacedContentCommand(
              request.selection.from,
              request.selection.to,
              {
                actionType:
                  request.command === AskAiCommand.EnhanceWithMyNotes
                    ? 'enhance-with-notes'
                    : undefined,
              },
            ),
          )
          .run();
      } else {
        const isStreaming =
          streamingInfo?.streamingMeta?.token &&
          streamingInfo?.streamingMeta?.channelName;
        const finalContent =
          isStreaming && streamEnded && streamContent.length > 0
            ? streamContent
            : processedContent;

        editor.commands.setContent(
          fragmentJSONToEditorContent(structuredClone(finalContent)),
        );
      }
      focusOnContainer();
    },
    [
      focusOnContainer,
      parentEditor,
      processedContent,
      streamingInfo?.streamingMeta?.channelName,
      streamingInfo?.streamingMeta?.token,
      streamContent,
      streamEnded,
      renderOriginalContentWithDifference,
      request.command,
      request.selection.from,
      request.selection.to,
    ],
  );

  const onEditorInitializedForStream = useCallback(
    async (editor: Editor) => {
      editor.commands.setContent(
        fragmentJSONToEditorContent(structuredClone(processedContent)),
      );

      await streamTipTapNodes({
        editor,
        identifierType: 'documentSectionId',
        identifierValues: streamingInfo?.promptIdList,
        shouldFilterByPromptId: true,
        token: streamingInfo?.streamingMeta?.token,
        channelName: streamingInfo?.streamingMeta?.channelName,
        onStart: () => {
          setIsInitializingStreaming(true);
        },
        onPostStream: async () => {
          const res = await StreamAPI.getPostProcessingContent({
            token: streamingInfo?.streamingMeta?.token,
            channelName: streamingInfo?.streamingMeta?.channelName,
            useCase: 'SEMANTIC_FORMATTING_AGENT',
            step: 'SEMANTIC_FORMATTING_PROCESSED',
          });
          const content = res?.[0]?.data?.response?.content;

          if (content) {
            editor.commands.setContent(
              fragmentJSONToEditorContent(structuredClone(content)),
            );
            setStreamContent(content);
          }
          setStreamEnded(true);
        },
      });
    },
    [
      processedContent,
      streamingInfo?.promptIdList,
      streamingInfo?.streamingMeta?.channelName,
      streamingInfo?.streamingMeta?.token,
    ],
  );
  const beforeOnAction = useCallback(() => {
    resultEditor
      ?.chainWithAskAiActionMeta()
      .command(removeAllAskAiSelectionHighlightsCommand())
      .command(({ tr }) => {
        const content = tr.doc.content.toJSON();

        onAction(content);

        return true;
      })
      .run();
  }, [onAction, resultEditor]);
  const onNewRequestRef = useRef(onNewRequest);

  onNewRequestRef.current = onNewRequest;

  return (
    <OutputSection
      ref={containerRef}
      collapsible={false}
      title={
        <SectionTitle
          title={getSectionTitle(request.command, loading)}
          icon={
            <SectionTitle.Icon icon={getSectionTitleIcon(request.command)} />
          }
          loading={loading}
        />
      }
      content={
        loading ? (
          <LoaderStreamingComponent />
        ) : (
          <>
            {streamEnded && (
              <>
                <DocumentEditor
                  floatingToolbarConfig={{
                    enabled: true,
                    toolbar: ({ editor }) => (
                      <AskAiToolbar
                        editor={editor}
                        config={{ showEnhanceWithMyNotes: true }}
                      />
                    ),
                  }}
                  extensionConfig={{
                    askAiConfig: {
                      enabled: true,
                      processAskAiRequest: (props) => {
                        InProgressStorageManager(
                          props.editor,
                        ).removeInProgressCommand(props.payload.command);
                        onNewRequestRef.current(props.payload, props.editor);
                      },
                      contentReplacementOptions:
                        parentEditorAskAiCommandExtension?.options
                          .contentReplacementOptions,
                    },
                  }}
                  onInitialize={onResultEditorInitialized}
                />
              </>
            )}
            {!streamEnded && (
              <>
                {!isInitializingStreaming && <LoaderStreamingComponent />}
                <DocumentEditor
                  readonly={!streamEnded}
                  onInitialize={onEditorInitializedForStream}
                />
              </>
            )}
          </>
        )
      }
      footer={
        !loading && streamEnded && resultEditor ? (
          <OutputSectionFooter
            editor={resultEditor}
            onAction={beforeOnAction}
          />
        ) : null
      }
    />
  );
};
